import { parseFiltersForActionLibraryInstances } from 'config/api/actionLibraryInstance/actionLibraryInstance.parsers';
import type { ParamsType } from 'helpers/createUrlWithParams/createUrlWithParams';

import type {
  DashboardKPIDTO,
  DashboardKPI,
  TemplateDashboardHoverAssetDTO,
  TemplateDashboardInstanceLink,
  TemplateDashboardInstanceLinkDTO,
  TemplatesDashboard,
  TemplatesDashboardQueryExtras,
  TemplatesDashboardRowDTO,
  TemplatesDashboardStatsFilterToken,
} from './statistics.types';

export const parseDashboardKpiForFE = (dto: DashboardKPIDTO): DashboardKPI => ({
  totalActiveInstances: dto.total_active_instances,
  totalOverdueInstances: dto.total_overdue_instances,
  avgActiveProgress: dto.avg_active_progress,
  upcomingDeadlines: dto.upcoming_deadlines ?? {},
});

const parseHoverAsset = (dto: TemplateDashboardHoverAssetDTO) => ({
  id: dto.id,
  key: dto.key,
  name: dto.name,
});

const parseInstanceLink = (dto: TemplateDashboardInstanceLinkDTO): TemplateDashboardInstanceLink => ({
  id: dto.id,
  title: dto.title,
  assetId: dto.asset_id,
  assetName: dto.asset_name,
  url: dto.url,
});

const parseTemplatesDashboardRow = (dto: TemplatesDashboardRowDTO) => ({
  templateId: dto.template_id,
  templateName: dto.template_name,
  area: dto.area,
  subArea: dto.sub_area,
  assetTypes: dto.asset_types ?? [],
  aValidInstancesCount: dto.a_valid_instances_count,
  kCompletedValidInstancesCount: dto.k_completed_valid_instances_count,
  tActionsLastYearCount: dto.t_actions_last_year_count,
  completionRate: dto.completion_rate,
  hoverAssets: (dto.hover_assets ?? []).map(parseHoverAsset),
  instancesValidTodaySample: (dto.instances_valid_today_sample ?? []).map(parseInstanceLink),
});

/**
 * Resolves the object that holds `rows` and aggregate fields — either the root body or
 * `results` when the API uses a DRF-style `{ count, next, previous, results: { rows, … } }` shape.
 */
const resolveTemplatesDashboardBucket = (payload: unknown): Record<string, unknown> => {
  if (payload == null || typeof payload !== 'object') return {};
  const root = payload as Record<string, unknown>;
  const { results } = root;
  if (results != null && typeof results === 'object' && !Array.isArray(results)) {
    const bucket = results as Record<string, unknown>;
    if (Array.isArray(bucket.rows)) return bucket;
  }
  return root;
};

const extractTemplatesDashboardRows = (payload: unknown): unknown[] => {
  const bucket = resolveTemplatesDashboardBucket(payload);
  if (Array.isArray(bucket.rows)) return bucket.rows;
  const root = payload != null && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  if (Array.isArray(root.results)) return root.results;
  return [];
};

const isTemplatesDashboardRowDTO = (value: unknown): value is TemplatesDashboardRowDTO => {
  if (value == null || typeof value !== 'object') return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.template_id === 'number' &&
    typeof r.template_name === 'string' &&
    r.area != null &&
    typeof r.area === 'object' &&
    typeof r.a_valid_instances_count === 'number' &&
    typeof r.k_completed_valid_instances_count === 'number' &&
    typeof r.t_actions_last_year_count === 'number' &&
    typeof r.completion_rate === 'number'
  );
};

/** Maps `GET /api/v1/statistics/templates-dashboard/` JSON to FE types; tolerates partial payloads. */
export const parseTemplatesDashboardForFE = (payload: unknown): TemplatesDashboard => {
  const bucket = resolveTemplatesDashboardBucket(payload);
  const rowList = extractTemplatesDashboardRows(payload).filter(isTemplatesDashboardRowDTO);
  const sumA = typeof bucket.sum_a_valid_instances_count === 'number' ? bucket.sum_a_valid_instances_count : 0;
  const sumK = typeof bucket.sum_k_completed_valid_instances_count === 'number' ? bucket.sum_k_completed_valid_instances_count : 0;
  const sumT = typeof bucket.sum_t_actions_last_year_count === 'number' ? bucket.sum_t_actions_last_year_count : 0;
  const rate = typeof bucket.completion_rate === 'number' ? bucket.completion_rate : 0;

  const root = payload != null && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  const totalTemplateCount = typeof root.count === 'number' ? root.count : undefined;

  return {
    rows: rowList.map(parseTemplatesDashboardRow),
    sumAValidInstancesCount: sumA,
    sumKCompletedValidInstancesCount: sumK,
    sumTActionsLastYearCount: sumT,
    completionRate: rate,
    ...(totalTemplateCount != null ? { totalTemplateCount } : {}),
  };
};

const isTemplateDashboardInstanceLinkDTO = (value: unknown): value is TemplateDashboardInstanceLinkDTO => {
  if (value == null || typeof value !== 'object') return false;
  const r = value as Record<string, unknown>;
  return (
    typeof r.id === 'number' &&
    typeof r.title === 'string' &&
    typeof r.asset_id === 'number' &&
    typeof r.asset_name === 'string' &&
    typeof r.url === 'string'
  );
};

const extractInstanceLinkList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  if (payload != null && typeof payload === 'object') {
    const o = payload as Record<string, unknown>;
    if (Array.isArray(o.results)) return o.results;
  }
  return [];
};

/** Maps `GET /api/v1/statistics/templates-dashboard-instances/` JSON to FE types. */
export const parseTemplateDashboardInstanceLinksForFE = (payload: unknown): TemplateDashboardInstanceLink[] =>
  extractInstanceLinkList(payload).filter(isTemplateDashboardInstanceLinkDTO).map(parseInstanceLink);

type UiFilters = Record<string, { id: string; name: string | string[] }[]>;

const firstCsvNumber = (value: string | undefined): number | undefined => {
  if (value == null || value === '') return undefined;
  const first = String(value).split(',')[0]?.trim();
  if (!first) return undefined;
  const n = Number(first);
  return Number.isFinite(n) ? n : undefined;
};

const STATS_FILTER_TOKENS: TemplatesDashboardStatsFilterToken[] = ['T>A', 'K<A', 'A=0'];

const normalizeStatsFiltersCsv = (tokens: TemplatesDashboardStatsFilterToken[]): string | undefined => {
  const ordered = tokens.filter(t => STATS_FILTER_TOKENS.includes(t));
  if (ordered.length === 0) return undefined;
  return ordered.filter((t, i) => ordered.indexOf(t) === i).join(',');
};

/**
 * Query keys only supported on `templates-dashboard/`, not on `templates-dashboard-instances/` (OpenAPI).
 * Strip these before drill-down requests when reusing the same param object.
 */
const TEMPLATES_DASHBOARD_MAIN_ONLY_PARAM_KEYS = ['include_instances', 'instances_limit', 'stats_filters', 'status'] as const;

/** Removes dashboard-main-only params so `templates-dashboard-instances/` matches OpenAPI. */
export const omitTemplatesDashboardMainOnlyParams = (params: ParamsType): ParamsType => {
  const next: ParamsType = { ...params };
  TEMPLATES_DASHBOARD_MAIN_ONLY_PARAM_KEYS.forEach(k => {
    delete next[k];
  });
  return next;
};

/** Maps instance-style UI filters to templates-dashboard query params. */
export const buildTemplatesStatisticsParams = (filters: UiFilters, extra?: TemplatesDashboardQueryExtras): ParamsType => {
  const parsed = parseFiltersForActionLibraryInstances(filters);
  const params: ParamsType = {};

  if (parsed.area != null && parsed.area !== '') {
    const n = Number(parsed.area);
    if (Number.isFinite(n)) params.area = n;
  }
  if (parsed.sub_area != null && parsed.sub_area !== '') {
    const n = Number(parsed.sub_area);
    if (Number.isFinite(n)) params.sub_area = n;
  }
  if (typeof parsed.asset__type === 'number' && Number.isFinite(parsed.asset__type)) {
    params.asset__type = parsed.asset__type;
  }
  const ownerId = firstCsvNumber(parsed.owner != null ? String(parsed.owner) : undefined);
  if (ownerId != null) params.owner = ownerId;
  const responsibleId = firstCsvNumber(parsed.responsible_user != null ? String(parsed.responsible_user) : undefined);
  if (responsibleId != null) params.responsible_user = responsibleId;
  const assetId = firstCsvNumber(parsed.asset_id != null ? String(parsed.asset_id) : undefined);
  if (assetId != null) params.asset_id = assetId;

  if (extra?.limit != null) params.limit = extra.limit;
  if (extra?.offset != null) params.offset = extra.offset;
  if (extra?.include_instances === true) params.include_instances = true;
  if (extra?.instances_limit != null) params.instances_limit = extra.instances_limit;

  if (extra?.search != null) {
    const trimmed = String(extra.search).trim();
    if (trimmed !== '') params.search = trimmed;
  }

  if (extra?.templateEffectiveStatus === 'inactive' || extra?.templateEffectiveStatus === 'all') {
    params.status = extra.templateEffectiveStatus;
  }

  const statsCsv = extra?.statsFilterTokens != null ? normalizeStatsFiltersCsv(extra.statsFilterTokens) : undefined;
  if (statsCsv != null) params.stats_filters = statsCsv;

  return params;
};

/** Maps UI filters to statistics/dashboard KPI query params (subset shared with instance list). */
export const buildAssetActionsDashboardKpiParams = (filters: UiFilters): ParamsType => {
  const parsed = parseFiltersForActionLibraryInstances(filters);
  const params: ParamsType = {};

  if (parsed.area != null && parsed.area !== '') {
    const n = Number(parsed.area);
    if (Number.isFinite(n)) params.area = n;
  }
  if (parsed.sub_area != null && parsed.sub_area !== '') {
    const n = Number(parsed.sub_area);
    if (Number.isFinite(n)) params.sub_area = n;
  }
  if (typeof parsed.asset__type === 'number' && Number.isFinite(parsed.asset__type)) {
    params.asset__type = parsed.asset__type;
  }
  if (parsed.criticality) params.criticality = String(parsed.criticality);
  const ownerId = firstCsvNumber(parsed.owner != null ? String(parsed.owner) : undefined);
  if (ownerId != null) params.owner = ownerId;
  if (parsed.created_from) params.date_from = String(parsed.created_from);
  if (parsed.created_to) params.date_to = String(parsed.created_to);

  return params;
};
